import { Globe, TriangleAlert } from 'lucide-react';
import type { Network } from '../../api/types';
import { Loading } from '../../components/ui';
import { strings } from '../../strings';
import type { GeneratedFlow } from './flows';

const r = strings.networks.requests;

/**
 * The Traffic tab's read-only part (spec 11 6.15.21, the "Segmentation" of feature spec 17 8.1): the generated flows,
 * grouped by deployment and written as "all apps of orders-prod in westeurope → db on TCP 5432", and the egress.
 */
export function TrafficFlows({ network, flows }: { network: Network; flows: GeneratedFlow[] | undefined }) {
  const firewalls = Object.entries(network.settings.providers).filter(([, p]) => p?.firewall?.enabled);
  const withFirewall = firewalls.length > 0;
  const byDeployment = new Map<string, GeneratedFlow[]>();
  for (const f of flows ?? []) {
    const key = `${f.deploymentName}\n${f.region}`;
    byDeployment.set(key, [...(byDeployment.get(key) ?? []), f]);
  }
  return (
    <>
      <section aria-labelledby="traffic-flows" className="space-y-2" data-testid="generated-flows">
        <h2 id="traffic-flows" className="flex flex-wrap items-baseline gap-x-2 text-[15px] font-semibold">
          {r.generatedFlows}
          <span className="text-[12.5px] font-normal text-fg-muted">
            {r.generatedFlowsNote(flows?.length ?? 0)}
          </span>
        </h2>
        {!flows ? (
          <Loading />
        ) : flows.length === 0 ? (
          <p className="text-sm text-fg-muted">{r.noFlows}</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-line bg-surface">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{r.columns.sourceTarget}</th>
                  <th scope="col">{r.port}</th>
                  <th scope="col">{r.columns.details}</th>
                </tr>
              </thead>
              <tbody>
                {[...byDeployment.values()].flatMap((list) =>
                  list.map((f) => (
                    <tr key={`${f.deploymentId}/${f.region}/${f.sourceId}/${f.targetId}`}>
                      <td>
                        {r.flowSource(f.deploymentName, f.region)} →{' '}
                        <span className="font-mono">{f.targetId}</span>
                      </td>
                      <td className="font-mono">{f.port ? r.tcp(f.port) : strings.common.dash}</td>
                      <td className="text-[12.5px] text-fg-muted">
                        {r.flowWhy(f.sourceId, f.targetId)}
                        {withFirewall && ` · ${r.checkedByFirewall}`}
                      </td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section aria-labelledby="traffic-egress" className="space-y-2" data-testid="egress">
        <h2 id="traffic-egress" className="text-[15px] font-semibold">
          {r.egress}
        </h2>
        {withFirewall ? (
          firewalls.map(([provider, p]) => (
            <div key={provider} className="rounded-lg border border-line bg-surface p-3 text-sm">
              <p className="flex items-center gap-2">
                <Globe aria-hidden className="size-4 text-fg-muted" />
                {p?.firewall?.egress === 'through-firewall' ? r.egressThroughFirewall : r.egressDirect}
              </p>
              <p className="mt-1">
                {p?.firewall?.allowedEgress?.length
                  ? r.egressAllowed(p.firewall.allowedEgress.join(', '))
                  : r.egressNone}
              </p>
              <p className="help mt-1">{r.egressNote}</p>
            </div>
          ))
        ) : (
          <p className="callout callout-warning">
            <TriangleAlert aria-hidden className="size-4 shrink-0" />
            <span>
              {r.egressNotFiltered} <code className="font-mono text-xs">EGRESS_NOT_FILTERED</code>
            </span>
          </p>
        )}
      </section>
    </>
  );
}
